import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, useCurrency } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Package, DollarSign, Users, UtensilsCrossed, TrendingUp, Clock, ChevronRight, ClipboardList, Store, Undo2, MessageSquare, BarChart3, UserCircle } from "lucide-react";

export default function AdminDashboard() {
  const { symbol } = useCurrency();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { credentials: "include" }),
        fetch(`${API}/orders/admin?status=received`, { credentials: "include" }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (ordersRes.ok) setRecentOrders((await ordersRes.json()).slice(0, 5));
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally { setLoading(false); }
  };

  const formatTime = (dateString) => new Date(dateString).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  const quickActions = [
    { label: "Orders", icon: ClipboardList, to: "/admin/orders", color: "bg-blue-500", count: stats?.pending_orders, badge: "Pending" },
    { label: "Menu Items", icon: UtensilsCrossed, to: "/admin/menu", color: "bg-green-600" },
    { label: "Kitchen Menus", icon: Store, to: "/admin/kitchen-menus", color: "bg-emerald-500" },
    { label: "Refunds", icon: Undo2, to: "/admin/refunds", color: "bg-orange-500" },
    { label: "Reviews", icon: MessageSquare, to: "/admin/reviews", color: "bg-purple-500" },
    { label: "Reports", icon: BarChart3, to: "/admin/reports", color: "bg-teal-500" },
  ];

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-dashboard">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Dashboard</h1>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-5">
            {[1, 2, 3, 4].map((i) => <div key={i} className="skeleton h-20 sm:h-28 rounded-xl" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5">
            <StatCard icon={<Package size={18} className="text-blue-500" />} label="Total Orders" value={stats?.total_orders || 0} bgColor="bg-blue-50" />
            <StatCard icon={<Clock size={18} className="text-orange-500" />} label="Pending" value={stats?.pending_orders || 0} bgColor="bg-orange-50" />
            <StatCard icon={<DollarSign size={18} className="text-green-500" />} label="Revenue" value={`${symbol}${Math.round(stats?.total_revenue || 0)}`} bgColor="bg-green-50" />
            <StatCard icon={<Users size={18} className="text-purple-500" />} label="Users" value={stats?.users || 0} bgColor="bg-purple-50" />
          </div>
        )}

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-5">
          <div className="bg-white rounded-xl border border-gray-100 p-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <UtensilsCrossed className="text-green-600" size={16} />
              </div>
              <div>
                <p className="text-gray-500 text-[10px]">Menu Items</p>
                <p className="text-lg font-bold text-gray-900">{stats?.menu_items || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="text-blue-600" size={16} />
              </div>
              <div>
                <p className="text-gray-500 text-[10px]">Delivered Today</p>
                <p className="text-lg font-bold text-gray-900">{stats?.delivered_orders || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Quick Actions — visible on mobile only */}
        <div className="md:hidden mb-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Quick Actions</h2>
          <div className="grid grid-cols-3 gap-2.5">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.label}
                  onClick={() => navigate(action.to)}
                  className="relative bg-white border border-gray-100 rounded-xl p-3 flex flex-col items-center gap-1.5 active:bg-gray-50 transition-colors"
                  data-testid={`quick-${action.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className={`w-10 h-10 ${action.color} rounded-xl flex items-center justify-center`}>
                    <Icon size={18} className="text-white" />
                  </div>
                  <span className="text-[10px] font-semibold text-gray-700 text-center leading-tight">{action.label}</span>
                  {action.count > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1">
                      {action.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900 text-sm sm:text-base">New Orders</h2>
            {recentOrders.length > 0 && (
              <button onClick={() => navigate("/admin/orders")} className="text-xs text-green-600 font-semibold flex items-center gap-0.5">
                View All <ChevronRight size={14} />
              </button>
            )}
          </div>
          {recentOrders.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-gray-500 text-sm">No pending orders</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recentOrders.map((order) => (
                <div
                  key={order.order_id}
                  className="p-3 sm:p-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100 transition-colors"
                  onClick={() => navigate("/admin/orders")}
                  data-testid={`recent-order-${order.order_id}`}
                >
                  <div className="flex justify-between items-center">
                    <div className="min-w-0 flex-1 mr-3">
                      <p className="font-medium text-gray-900 text-sm truncate">{order.user_name}</p>
                      <p className="text-gray-500 text-xs">{order.items?.length || 0} item{(order.items?.length || 0) > 1 ? "s" : ""} · {symbol}{Math.round(order.total_amount)}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-bold rounded-full">New</span>
                      <p className="text-gray-400 text-[10px] mt-0.5">{formatTime(order.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ icon, label, value, bgColor }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`w-9 h-9 sm:w-10 sm:h-10 ${bgColor} rounded-lg flex items-center justify-center mb-1.5 sm:mb-2`}>
        {icon}
      </div>
      <p className="text-gray-500 text-[10px] sm:text-xs">{label}</p>
      <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}
